import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { X } from 'lucide-react';

interface InteractiveArea {
  id: number;
  title: string;
  description: string;
  items?: string[];
  dotPosition: {
    top: number;
    left: number;
  };
  textPosition: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  textOffsetX?: number;
  textOffsetY?: number;
}

const interactiveAreas: InteractiveArea[] = [
  {
    id: 1,
    title: "BASEMENT",
    items: ["Crystallization waterproofing", "Waterproofing solutions","Damp proofing systems", "Gas proofing systems", "Concrete repair systems", "Anchoring and Grouting"],
    dotPosition: { top: 70, left: 39 },
    textPosition: 'right',
    textOffsetX: 10,
    textOffsetY: 0
  },
  {
    id: 2,
    title: "ROAD & CARPARK",
    items: ["Anti-skid surfaces", "Line marking", "Asphalt sealers", "Traffic-grade screeds", "Concrete repair solutions"],
    dotPosition: { top: 55, left: 65 },
    textPosition: 'left',
    textOffsetX: -10,
    textOffsetY: 0
  },
  {
    id: 3,
    title: "BATHROOMS",
    items: ["Waterproofing solutions", "Joint sealants"],
    dotPosition: { top: 21, left: 62 },
    textPosition: 'right',
    textOffsetX: 10,
    textOffsetY: 0
  },
  {
    id: 4,
    title: "PLANTERS",
    items: ["Waterproofing solutions", "Root-resistant coatings", "Drainage systems"],
    dotPosition: { top: 56, left: 47 },
    textPosition: 'left',
    textOffsetX: -10,
    textOffsetY: 0
  },
  {
    id: 5,
    title: "WATER TANKS",
    items: ["Crystallization waterproofing", "Waterproofing solutions"],
    dotPosition: { top: 16, left: 61},
    textPosition: 'top-right',
    textOffsetX: 10,
    textOffsetY: 0
  },
  {
    id: 6,
    title: "WALL",
    items: ["Coatings", "Concrete repair systems", "Damp proofing systems"],
    dotPosition: { top: 45, left: 45 },
    textPosition: 'left',
    textOffsetX: -10,
    textOffsetY: 0
  },
  {
    id: 7,
    title: "LIFT PIT",
    items: ["Waterproofing solutions", "Concrete repair mortars", "Anchoring and Grouting"],
    dotPosition: { top: 64, left: 56 },
    textPosition: 'right',
    textOffsetX: 10,
    textOffsetY: 0
  },
  {
    id: 8,
    title: "ROOF",
    items: ["Waterproofing solutions", "Protective coatings", "Joint sealants", "Repair systems"],
    dotPosition: { top: 18, left: 50 },
    textPosition: 'top',
    textOffsetX: 0,
    textOffsetY: -10
  },
  {
    id: 9,
    title: "BALCONIES",
    items: ["Floor coatings", "Waterproofing solutions", "Repair system"],
    dotPosition: { top: 26, left: 47 },
    textPosition: 'left',
    textOffsetX: -10,
    textOffsetY: 0
  },
  {
    id: 10,
    title: "SWIMMING POOLS",
    items: ["Repair systems","Waterproofing solutions", "Coating systems", "Joint sealants"],
    dotPosition: { top: 37, left: 75 },
    textPosition: 'right',
    textOffsetX: 10,
    textOffsetY: 0
  },
  {
    id: 11,
    title: "PODIUMS",
    items: ["Waterproofing solutions","Protective coatings", "Joint sealants", "Repair systems"],
    dotPosition: { top: 40, left: 65 },
    textPosition: 'right',
    textOffsetX: 10,
    textOffsetY: 0
  }
];

const Work: React.FC = () => {
  const navigate = useNavigate();
  const [dotsVisible, setDotsVisible] = useState(false);
  const [hoveredAreaId, setHoveredAreaId] = useState<number | null>(null);
  const [selectedAreaForMobileModal, setSelectedAreaForMobileModal] = useState<InteractiveArea | null>(null);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [descriptionBoxPosition, setDescriptionBoxPosition] = useState<{top: number, left: number} | null>(null);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDotsVisible(true);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (showMobileModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileModal]);

  // Calculate description box position when hoveredAreaId changes
  useEffect(() => {
    if (hoveredAreaId && imageRef.current && containerRef.current) {
      const hoveredArea = interactiveAreas.find(area => area.id === hoveredAreaId);
      if (hoveredArea) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const imageRect = imageRef.current.getBoundingClientRect();
        
        // Calculate dot position in pixels relative to the container
        const dotX = (hoveredArea.dotPosition.left / 100) * imageRect.width;
        const dotY = (hoveredArea.dotPosition.top / 100) * imageRect.height;
        
        // Add offset from dot position (20px right and 20px down)
        const boxLeft = dotX + 20;
        const boxTop = dotY + 20;
        
        // Ensure the box doesn't go outside the container bounds
        const maxLeft = imageRect.width - 320; // 320px is the box width (w-80)
        const maxTop = imageRect.height - 200; // Approximate box height
        
        setDescriptionBoxPosition({
          left: Math.min(boxLeft, maxLeft),
          top: Math.max(20, Math.min(boxTop, maxTop))
        });
      }
    } else {
      setDescriptionBoxPosition(null);
    }
  }, [hoveredAreaId]);

  const handleDotClick = (area: InteractiveArea) => {
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      setSelectedAreaForMobileModal(area);
      setShowMobileModal(true);
    }
  };

  const closeMobileModal = () => {
    setShowMobileModal(false);
    setSelectedAreaForMobileModal(null);
  };

  const handleAreaHover = (areaId: number | null) => {
    setHoveredAreaId(areaId);
  };

  const getTextPositionStyles = (area: InteractiveArea) => {
    const { textPosition, textOffsetX = 0, textOffsetY = 0 } = area;
    
    let transform = '';
    let top = '0%';
    let left = '0%';
    
    switch (textPosition) {
      case 'top':
        transform = 'translate(-50%, -100%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'bottom':
        transform = 'translate(-50%, 0%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'left':
        transform = 'translate(-100%, -50%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'right':
        transform = 'translate(0%, -50%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'top-left':
        transform = 'translate(-100%, -100%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'top-right':
        transform = 'translate(0%, -100%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'bottom-left':
        transform = 'translate(-100%, 0%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      case 'bottom-right':
        transform = 'translate(0%, 0%)';
        top = `${textOffsetY}px`;
        left = `${textOffsetX}px`;
        break;
      default:
        transform = 'translate(-50%, -50%)';
    }
    
    return {
      position: 'absolute' as const,
      top,
      left,
      transform,
      zIndex: hoveredAreaId === area.id ? 30 : 20,
    };
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-24">
        <div className="max-w-6xl mx-auto" ref={containerRef}>
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 text-center">Our Expertise</h2>
            <div className="w-24 h-1 bg-[#ff2910] mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg leading-relaxed text-center max-w-3xl mx-auto text-align-last-center">
             At Hop Shing Engineering and Construction, we specialize in providing complete building protection and enhancement solutions throughout every phase of construction and maintenance. With extensive experience across residential, commercial, and infrastructure projects, our expertise covers all critical building elements from foundation to roof.
            </p>
            
            {/* Mobile Layout */}
            <div className="mt-8 md:hidden relative">
              <img 
                src="/assets/images/Tall building flattened_1.jpg"
                alt="Our Expertise Overview"
                className="w-full h-auto object-cover rounded-lg shadow-lg"
              />
              {interactiveAreas.map((area, index) => (
                <div 
                  key={area.id} 
                  className={`absolute transition-all duration-500 ${
                    dotsVisible 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-4'
                  }`}
                  style={{
                    top: `${area.dotPosition.top}%`,
                    left: `${area.dotPosition.left}%`,
                    transitionDelay: `${index * 100}ms`
                  }}
                >
                  <button
                    onClick={() => handleDotClick(area)}
                    className="w-4 h-4 bg-[#ff2910] rounded-full border-2 border-white shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 transform -translate-x-1/2 -translate-y-1/2 ripple-button"
                    title={area.title}
                    aria-label={`View details for ${area.title}`}
                  />
                </div>
              ))}
            </div>

            {/* Desktop Layout */}
            <div className="mt-8 hidden md:block relative">
              <img 
                ref={imageRef}
                src="/assets/images/Tall building flattened_1.jpg"
                alt="Our Expertise Overview"
                className="w-full h-auto object-cover rounded-lg shadow-lg"
              />
              
              {interactiveAreas.map((area, index) => (
                <div 
                  key={area.id} 
                  className={`absolute transition-all duration-500 ${
                    dotsVisible 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-4'
                  } ${hoveredAreaId === area.id ? 'z-30' : 'z-20'}`}
                  style={{
                    top: `${area.dotPosition.top}%`,
                    left: `${area.dotPosition.left}%`,
                    transitionDelay: `${index * 100}ms`
                  }}
                  onMouseEnter={() => handleAreaHover(area.id)}
                  onMouseLeave={() => handleAreaHover(null)}
                >
                  {/* Dot */}
                  <button
                    className={`w-3 h-3 border-2 transition-all duration-300 transform -translate-x-1/2 -translate-y-1/2 ${
                      hoveredAreaId === area.id 
                        ? 'border-[#ff2910] bg-[#ff2910] scale-110 shadow-lg rounded-full' 
                        : 'border-[#ff2910] bg-transparent hover:bg-[#ff2910] hover:scale-105 hover:rounded-full'
                    }`}
                    title={area.title}
                  />
                  
                  {/* Text Label */}
                  <div
                    className={`text-sm font-semibold transition-all duration-300 whitespace-nowrap bg-[#FF2910] text-white bg-opacity-75 px-2 py-1 rounded ${
                      hoveredAreaId === area.id ? 'bg-opacity-100 shadow-lg' : ''
                    }`}
                    style={getTextPositionStyles(area)}
                  >
                    {area.title}
                  </div>
                </div>
              ))}

              {/* Description Box */}
              {hoveredAreaId && descriptionBoxPosition && (
                <div 
                  className="absolute bg-white rounded-lg shadow-xl p-4 w-80 z-40 border border-gray-200 transition-all duration-300"
                  style={{
                    top: `${descriptionBoxPosition.top}px`,
                    left: `${descriptionBoxPosition.left}px`
                  }}
                >
                  {(() => {
                    const area = interactiveAreas.find(a => a.id === hoveredAreaId);
                    if (!area) return null;
                    return (
                      <>
                        <h3 className="text-lg font-bold text-gray-800 mb-3">{area.title}</h3>
                        <p className="text-gray-600 text-sm mb-3 leading-relaxed">{area.description}</p>
                        {area.items && area.items.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-800 mb-2 text-sm">Key Services:</h4>
                            <ul className="space-y-1">
                              {area.items.map((item, itemIndex) => (
                                <li 
                                  key={itemIndex}
                                  className="flex items-center space-x-2 text-gray-600 text-sm"
                                >
                                  <span className="w-1.5 h-1.5 bg-[#ff2910] rounded-full flex-shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-600 text-lg mb-8 text-align-last-center">
              To explore our complete range of branded products and technical specifications,
              please visit our product website
            </p>
            <button 
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="bg-[#FF2910] text-white px-8 py-3 rounded-lg text-lg font-medium transition-all duration-300 hover:bg-[#FF2910] transform hover:scale-105"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Modal */}
      {showMobileModal && selectedAreaForMobileModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 md:hidden"
          onClick={closeMobileModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 relative transform transition-all duration-300 scale-100 max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeMobileModal}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={24} />
            </button>
            
            <h3 className="text-xl font-bold text-gray-800 pr-8 mb-4">{selectedAreaForMobileModal.title}</h3>
            
            <p className="text-gray-600 mb-4 leading-relaxed">{selectedAreaForMobileModal.description}</p>
            
            {selectedAreaForMobileModal.items && selectedAreaForMobileModal.items.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Key Services:</h4>
                <ul className="space-y-2">
                  {selectedAreaForMobileModal.items.map((item, itemIndex) => (
                    <li 
                      key={itemIndex}
                      className="flex items-center space-x-2 text-gray-600"
                    >
                      <span className="w-2 h-2 bg-[#ff2910] rounded-full flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <ScrollToTop />
      <Footer />
    </div>
  );
};

export default Work;